import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import { ApiError, apiFetch } from "@/api/client";
import { markJustSignedOut, peekJustSignedOut } from "@/api/cognito";
import { useLastAppStore } from "@/stores/last-app";
import { useLastProjectStore } from "@/stores/last-project";

// Wire-format mirrors the SessionResponse contract in
// src/flowstack/server/routes/auth.py. Cognito is the only auth backend
// in production after the M9 #154 pilot rip-out — the shape is
// preserved verbatim for back-compat with the rest of the GUI that
// reads ``session.email`` / ``session.display_name`` / etc.

export interface Session {
  session_id: string;
  user_id: string;
  email: string;
  /** The effective name: the user's Settings override, else the sign-in name. */
  display_name: string;
  /** The name the Cognito token chain produced. Absent from an older server. */
  sign_in_display_name?: string;
  /** True while a Settings override is in force (flowstack#2002). */
  display_name_overridden?: boolean;
  tenant_id: string;
  tenant_slug: string;
  tenant_display_name: string;
  issued_at: string;
  expires_at: string;
}

/**
 * Type alias for back-compat. The codebase used to call this
 * ``PilotSession`` when the pilot email/password shim was the only
 * auth path; ``Session`` is the same wire shape now sourced from
 * Cognito. Kept as an alias so external imports survive the rename.
 */
export type PilotSession = Session;

export const SESSION_QUERY_KEY = ["auth", "session"] as const;

/** Shown when the signed-in account belongs to no organization (fs#1206). */
export const NO_ORGANIZATION_MESSAGE =
  "Your account does not belong to an organization yet: ask your administrator.";
export const TRUSTED_BROWSER_QUERY_KEY = ["auth", "trusted-browser"] as const;

/** Wire shape of ``/api/auth/trusted-browser`` (all three verbs).
 *  ``available: false`` means the server can't back the feature (no
 *  database configured) — the Settings toggle hides itself. */
export interface TrustedBrowserStatus {
  trusted: boolean;
  available: boolean;
  expires_at?: string | null;
}

async function logoutRequest(): Promise<void> {
  return apiFetch<void>("/api/auth/logout", { method: "POST" });
}

/** ``POST /api/auth/refresh`` — trade the long-lived trusted-browser
 *  cookie for a fresh 8h session. Returns the session wire shape on
 *  success; ``null`` when the browser isn't trusted (401), the
 *  backend predates the route (404), or the feature is unavailable
 *  during the schema deploy gate (503). Any other failure propagates
 *  (a thrown non-{401,404,503} clears the dedupe slot below, so the
 *  next caller legitimately retries). */
async function refreshSession(): Promise<Session | null> {
  try {
    return await apiFetch<Session>("/api/auth/refresh", { method: "POST" });
  } catch (err) {
    if (
      err instanceof ApiError &&
      (err.status === 401 || err.status === 404 || err.status === 503)
    ) {
      return null;
    }
    throw err;
  }
}

/** Module-level dedupe for the silent refresh: refetch-on-focus plus
 *  several session consumers can observe the same 401 in one beat,
 *  and each would otherwise fire its own POST. All concurrent callers
 *  share one in-flight refresh; the slot clears when it settles so a
 *  later expiry can refresh again. */
let refreshInFlight: Promise<Session | null> | null = null;

function refreshSessionOnce(): Promise<Session | null> {
  if (!refreshInFlight) {
    refreshInFlight = refreshSession().finally(() => {
      refreshInFlight = null;
    });
  }
  return refreshInFlight;
}

/** Test-only: clear the module-level dedupe slot so cases across test
 *  files start deterministic (Rupert PR-596 r0). */
export function __resetRefreshInFlightForTesting(): void {
  refreshInFlight = null;
}

/** Exported for tests — production consumers go through ``useSession``. */
export async function fetchSession(): Promise<Session | null> {
  try {
    return await apiFetch<Session>("/api/auth/session");
  } catch (err) {
    if (err instanceof ApiError && err.status === 404) {
      // 404 on the session route means the route doesn't exist
      // (backend predates auth / proxy misconfiguration) — NOT
      // "session dead". Resolve null WITHOUT a refresh POST; the
      // backend 401s an absent/expired session, never 404s it
      // (src/flowstack/server/routes/auth.py). Per Rupert PR-596 r0.
      return null;
    }
    if (err instanceof ApiError && err.status === 401) {
      // Dead session. If the user *just signed out*, do NOT silently
      // refresh — logout revoked the trust token server-side, and the
      // sign-in page is about to render the explicit signed-out card.
      if (peekJustSignedOut()) {
        return null;
      }
      // Otherwise attempt the trusted-browser refresh. Its response
      // IS the session wire shape, so there is no follow-up GET (and
      // therefore no refresh→session→refresh loop to guard against):
      // either we get a session or the user goes to the Hosted UI.
      const refreshed = await refreshSessionOnce();
      // Re-check after awaiting: sign-out may have raced the
      // in-flight refresh, and returning the resurrected session
      // would undo the logout in the query cache (Rupert PR-596 r0).
      if (peekJustSignedOut()) {
        return null;
      }
      return refreshed;
    }
    throw err;
  }
}

export function useLogout() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: logoutRequest,
    onSettled: async () => {
      // Cancel + remove every non-session query before flipping the
      // session to null. Without this, in-flight queries on the page
      // we're leaving (e.g. ``useProjects`` on ``/projects``) keep
      // running, hit the API with no cookie, get back 401 — and
      // before ``RequireSession`` can navigate to the sign-in page
      // the route's ``isError`` UI flashes the 401 for a beat.
      // Cancelling stops the fetch; removing wipes any cached error
      // state so the route renders nothing in the gap.
      await queryClient.cancelQueries();
      queryClient.removeQueries({
        predicate: (query) => query.queryKey[0] !== SESSION_QUERY_KEY[0],
      });
      // The predicate above keeps every "auth"-prefixed key, which
      // includes the trusted-browser status — remove it explicitly:
      // logout revoked the trust token server-side, so a cached
      // ``trusted: true`` would render a stale toggle on the next
      // sign-in.
      queryClient.removeQueries({ queryKey: TRUSTED_BROWSER_QUERY_KEY });
      queryClient.setQueryData(SESSION_QUERY_KEY, null);
      // The last-used project and app belong to the viewer who just left.
      useLastProjectStore.getState().clear();
      useLastAppStore.getState().clear();
      // Tell the next /auth/cognito/sign-in render to show the explicit
      // "you're signed out" card instead of auto-redirecting. Without
      // this the sign-in page would silently re-SSO (Cognito's session
      // cookie outlives our cookie) and the Sign out click would
      // appear to do nothing — same fix pharmaforge ships for #101.
      markJustSignedOut();
    },
  });
}

export function useSession() {
  return useQuery({
    queryKey: SESSION_QUERY_KEY,
    queryFn: fetchSession,
    staleTime: 60_000,
    refetchOnWindowFocus: true,
  });
}

// ---- Display name (flowstack#2002) ----------------------------------

/** ``PATCH /api/me`` — set the display-name override, or clear it with an
 *  empty string / ``null``. The response is the session wire shape, written
 *  straight into the session cache so every reader of ``display_name`` (the
 *  Settings row, the sidebar chip, the Dashboard greeting, Ruby) follows. */
export function useUpdateDisplayName() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (displayName: string | null) =>
      apiFetch<Session>("/api/me", { method: "PATCH", json: { display_name: displayName } }),
    onSuccess: (session) => {
      queryClient.setQueryData(SESSION_QUERY_KEY, session);
    },
  });
}

// ---- Trusted browser ("remember me") --------------------------------

async function fetchTrustedBrowserStatus(): Promise<TrustedBrowserStatus> {
  try {
    return await apiFetch<TrustedBrowserStatus>("/api/auth/trusted-browser");
  } catch (err) {
    if (err instanceof ApiError && (err.status === 404 || err.status === 503)) {
      // 404: backend predates the feature. 503: feature deployed but
      // its storage isn't (schema deploy gate). Either way behave
      // like "unavailable" so the Settings toggle hides itself
      // instead of erroring.
      return { trusted: false, available: false };
    }
    throw err;
  }
}

export function useTrustedBrowser() {
  return useQuery({
    queryKey: TRUSTED_BROWSER_QUERY_KEY,
    queryFn: fetchTrustedBrowserStatus,
    staleTime: 60_000,
  });
}

export function useEnableTrustedBrowser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () =>
      apiFetch<TrustedBrowserStatus>("/api/auth/trusted-browser", { method: "POST" }),
    onSuccess: (status) => {
      // The enable response is the fresh status — write it straight
      // into the cache rather than invalidating + refetching.
      queryClient.setQueryData(TRUSTED_BROWSER_QUERY_KEY, status);
    },
    onError: () => {
      // A failed-looking mutation may still have succeeded server-side
      // (response lost). For a security control, re-ask the server
      // instead of guessing at the cache state (Rupert PR-596 r0).
      void queryClient.invalidateQueries({ queryKey: TRUSTED_BROWSER_QUERY_KEY });
    },
  });
}

export function useForgetTrustedBrowser() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: () => apiFetch<void>("/api/auth/trusted-browser", { method: "DELETE" }),
    onSuccess: () => {
      // DELETE returns 204 with no body; the post-forget status is
      // fully determined (cookie cleared server-side), so writing it
      // directly is safe and avoids a refetch.
      queryClient.setQueryData<TrustedBrowserStatus>(TRUSTED_BROWSER_QUERY_KEY, {
        trusted: false,
        available: true,
        expires_at: null,
      });
    },
    onError: () => {
      void queryClient.invalidateQueries({ queryKey: TRUSTED_BROWSER_QUERY_KEY });
    },
  });
}
